/**
 * Pure XZ-plane geometry helpers for the room-merge engine.
 *
 * The floor plane is X-Z (Y is up). "Increasing Z is screen-down" — see
 * CLAUDE.md. None of these helpers care about that convention; they are
 * winding-agnostic where it matters (area uses absolute value).
 */

const COLLINEAR_DOT = 0.995; // matches room-polygon-builder's threshold

/** Signed area of an XZ polygon ([{x,z}, ...]). Negative = CCW in z-down. */
function signedAreaXZ(poly) {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const j = (i + 1) % poly.length;
    a += poly[i].x * poly[j].z - poly[j].x * poly[i].z;
  }
  return a / 2;
}

/** Absolute area of an XZ polygon. */
function areaXZ(poly) {
  return Math.abs(signedAreaXZ(poly));
}

/** Area-weighted centroid of an XZ polygon. Falls back to vertex mean for
 *  degenerate (zero-area) polygons. */
function centroidXZ(poly) {
  const a2 = signedAreaXZ(poly) * 2; // = sum of cross terms
  if (Math.abs(a2) < 1e-9) {
    let cx = 0;
    let cz = 0;
    for (const p of poly) {
      cx += p.x;
      cz += p.z;
    }
    return { x: cx / poly.length, z: cz / poly.length };
  }
  let cx = 0;
  let cz = 0;
  for (let i = 0; i < poly.length; i++) {
    const j = (i + 1) % poly.length;
    const cross = poly[i].x * poly[j].z - poly[j].x * poly[i].z;
    cx += (poly[i].x + poly[j].x) * cross;
    cz += (poly[i].z + poly[j].z) * cross;
  }
  return { x: cx / (3 * a2), z: cz / (3 * a2) };
}

// Canonical point-in-polygon (issue #1030); re-exported under the
// module's historical name for its callers.
const {
  isPointInsidePolygon: pointInPolygonXZ,
} = require('../geometry/pointInPolygon');

/** Axis-aligned bounding box of an XZ polygon → {minX,maxX,minZ,maxZ}. */
function bboxXZ(poly) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of poly) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
  }
  return { minX, maxX, minZ, maxZ };
}

module.exports = {
  COLLINEAR_DOT,
  signedAreaXZ,
  areaXZ,
  centroidXZ,
  pointInPolygonXZ,
  bboxXZ,
};
